import { ParsedUrlQueryInput } from 'querystring';
import { NextRouter } from 'next/router';
import { ANALYTICS_STRINGS, AnalyticsEvents } from '@belong/analytics';
import { getConfig } from '@belong/configs/next/config';
import { isRegularNumber } from '@belong/type-utils';
import { withoutBasePath } from '@belong/url-utils';
import { IStepperState } from './Stepper.types';

export const dispatchStepperEvent = <T>(
  analyticsEvents: AnalyticsEvents,
  state: Partial<IStepperState<T>>,
  steps: any[]
): any => {
  const content = typeof state.currentStep === 'number' ? steps[state.currentStep] : {};

  analyticsEvents.send({
    action: ANALYTICS_STRINGS.ACTION.STEP_CHANGE,
    category: ANALYTICS_STRINGS.CATEGORY.INTERACTION,
    label: ANALYTICS_STRINGS.LABEL.STEP_CHANGE,
    state,
    content: content || {}
  });
};

export const updateUrlWithoutRefresh = ({
  nextRouter,
  queryParams,
  isFirstStep,
  keepQueryString = false
}: {
  nextRouter: NextRouter;
  queryParams?: ParsedUrlQueryInput;
  isFirstStep: boolean;
  keepQueryString?: boolean;
}): void => {
  if (!nextRouter.isReady) {
    return;
  }

  const { basePath } = getConfig().publicRuntimeConfig;
  const routerUrl = {
    pathname: withoutBasePath(window.location.pathname, basePath),
    query: { ...(keepQueryString ? nextRouter.query : {}), ...queryParams }
  };

  const routerOptions = {
    // update page url without triggering a page refresh
    shallow: true
  };

  let routerAction = 'push';
  if (isFirstStep) {
    routerAction = 'replace';
  }

  nextRouter[routerAction](routerUrl, undefined, routerOptions);
};

export const getStepFromUrl = (url: string, paramName: string, steps?: any[] | undefined): number | undefined => {
  const queryParams = new URLSearchParams(url.split('?')[1]);
  const param = queryParams.get(paramName);

  if (steps) {
    const currentStep = steps.findIndex(step => step.stepName === param);

    if (currentStep < 0 || !isRegularNumber(currentStep)) {
      return undefined;
    }

    // For steps from url query string, it's current step + 1
    // when find the steps from array, also + 1 to match
    return currentStep + 1;
  }

  if (!param) {
    return undefined;
  }

  const step = Number(param);

  if (!isRegularNumber(step)) {
    return undefined;
  }

  return step;
};

export const updateStateWithBrowserHistory = ({
  stepInNewUrl,
  onRetreat,
  goToIndex
}: {
  stepInNewUrl: number | undefined;
  onRetreat: () => void;
  goToIndex: ({ index }) => void;
}): void => {
  if (!stepInNewUrl) {
    onRetreat();
    return;
  }

  goToIndex({ index: stepInNewUrl - 1 });
};
